import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PublicNav from "@/components/PublicNav";
import Seo from "@/components/Seo";
import FaqSection, { faqJsonLd } from "@/components/FaqSection";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getAttribution, getLastArticle, getSessionId, trackEvent } from "@/lib/attribution";
import { toast } from "sonner";

const QUIZ_FAQ_ITEMS = [
  {
    q: "How long does the quiz take?",
    a: "About 3 minutes. Eight short questions, mostly multiple-choice. You can stop and pick up later — your answers save automatically.",
  },
  {
    q: "Is the quiz really free?",
    a: "Yes, completely. CareHomeConnect is free to families. Senior living communities and placement directors pay us a small referral fee — but only after a successful move-in. You're never billed.",
  },
  {
    q: "What information do I need to share?",
    a: "Nothing at the start — just take the quiz. At the end, we'll ask for your first name and email so we can send your results. Phone is optional.",
  },
  {
    q: "How accurate is the recommendation?",
    a: "The quiz uses the same eight criteria experienced senior-living advisors use to recommend a level of care. It's a strong starting point. After your results, a vetted advisor will validate the recommendation against your specific situation and adjust if needed.",
  },
  {
    q: "Will my information be sold or shared with multiple companies?",
    a: "Never. We share your details with one matched local advisor only — not the 5+ companies big lead-gen sites send your info to. You can opt out of all contact at any time with one reply.",
  },
  {
    q: "What if I'm just researching and not ready to move yet?",
    a: "That's the most common situation. Take the quiz anyway — knowing the right level of care for today helps you spot when (or if) it's time. Your advisor will respect your timeline and check in only as you're ready.",
  },
];

export default function Quiz() {
  const navigate = useNavigate();
  // step 1..N = questions, step N+1 = capture form
  const [step, setStep] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [capture, setCapture] = useState({
    first_name: "",
    email: "",
    phone: "",
    text_me_results: false,
  });

  useEffect(() => {
    api.get("/quiz/public").then(({ data }) => setQuestions(data.questions || [])).catch(() => {});
    trackEvent("quiz_viewed", { page: "/quiz" });
  }, []);

  const captureStep = questions.length + 1;
  const totalSteps = questions.length + 1; // questions + capture
  const progress = ((step - 1) / totalSteps) * 100;

  const selectOption = (qid, value) => {
    setAnswers((p) => ({ ...p, [qid]: value }));
    // Auto-advance after a brief delay
    setTimeout(() => {
      setStep((s) => s + 1);
    }, 220);
  };

  // After last question answered, jump to capture; if capture step reached without
  // any answers (edge case from skipping), nothing happens until user goes back.
  const submitFinal = async (e) => {
    if (e) e.preventDefault();
    if (!capture.first_name.trim() || !capture.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    if (capture.text_me_results && !capture.phone.trim()) {
      toast.error("Phone number is required if you'd like a text.");
      return;
    }
    setLoading(true);
    try {
      const attribution = getAttribution();
      const source_article = getLastArticle();
      const session_id = getSessionId();
      // 1. Create the lead with contact info
      const { data: created } = await api.post("/leads/quiz-start", {
        first_name: capture.first_name,
        email: capture.email,
        phone: capture.phone || null,
        text_me_results: capture.text_me_results,
        source: "quiz",
        ...attribution,
        source_article,
        session_id,
      });
      const leadId = created.lead_id;
      trackEvent("capture_submitted", { lead_id: leadId });
      // 2. Submit the answers immediately
      const { data: submitted } = await api.post("/leads/quiz-submit", {
        lead_id: leadId,
        answers,
      });
      trackEvent("quiz_completed", { lead_id: leadId, step: submitted.care_type });
      // 3. Navigate to results
      navigate(`/results/${leadId}`, {
        state: { care_type: submitted.care_type, explanation: submitted.explanation },
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const currentQ = step >= 1 && step <= questions.length ? questions[step - 1] : null;
  const onCapture = step === captureStep;

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo
        title="Free senior care quiz — get a personalized recommendation in 3 minutes"
        description="Answer a few simple questions about your loved one and we'll recommend the right type of senior care — assisted living, memory care, or independent living. Free, gentle, no pressure."
        path="/quiz"
        keywords="senior care quiz, find senior care, assisted living quiz, memory care quiz, senior living advisor"
        jsonLd={faqJsonLd(QUIZ_FAQ_ITEMS)}
      />
      <PublicNav />
      <div className="max-w-2xl mx-auto px-6 md:px-8 pt-8 md:pt-16 pb-24">
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between text-xs text-[#5C6B62] mb-2">
            <span>Step {Math.min(step, totalSteps)} of {totalSteps}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <div className="h-2 bg-[#E2DFD8] rounded-full overflow-hidden" data-testid="quiz-progress">
            <div
              className="h-full bg-[#9CAF88] transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {currentQ && (
          <div className="animate-fade-in-up" key={currentQ.id} data-testid={`quiz-question-${currentQ.id}`}>
            {step > 1 && (
              <button
                onClick={() => setStep(Math.max(1, step - 1))}
                className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2C4C3B] mb-6"
                data-testid="quiz-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] tracking-tight mb-6">
              {currentQ.label}
            </h2>
            <div className="space-y-3">
              {currentQ.options?.map((opt) => {
                const selected = answers[currentQ.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => selectOption(currentQ.id, opt.value)}
                    data-testid={`quiz-option-${currentQ.id}-${opt.value}`}
                    className={`w-full text-left p-5 rounded-2xl border-2 transition-all font-medium ${
                      selected
                        ? "border-[#2C4C3B] bg-[#9CAF88]/15 text-[#1A2E24]"
                        : "border-[#E2DFD8] bg-white text-[#2C4C3B] hover:border-[#9CAF88] hover:bg-[#9CAF88]/5"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {!currentQ.required && (
              <button
                onClick={() => setStep(step + 1)}
                data-testid="quiz-skip-btn"
                className="mt-6 text-sm text-[#5C6B62] hover:text-[#2C4C3B] underline"
              >
                Skip this question
              </button>
            )}
          </div>
        )}

        {onCapture && (
          <div className="animate-fade-in-up" data-testid="quiz-step-capture">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#9CAF88]/15 text-[#2C4C3B] text-xs font-medium mb-4">
                <Heart className="w-3.5 h-3.5" fill="currentColor" /> Your results are ready
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] tracking-tight">
                Where should we send your results?
              </h1>
              <p className="mt-3 text-[#5C6B62] max-w-md mx-auto">
                We'll email your personalized care recommendation right now — and a local specialist will follow up only if you ask them to.
              </p>
            </div>

            <form onSubmit={submitFinal} className="card-soft p-6 md:p-8 space-y-5">
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2C4C3B]"
                data-testid="quiz-back-from-capture"
              >
                <ArrowLeft className="w-4 h-4" /> Back to questions
              </button>

              <div>
                <Label htmlFor="first_name" className="text-[#2C4C3B] font-medium">First name</Label>
                <Input
                  id="first_name"
                  data-testid="capture-first-name"
                  value={capture.first_name}
                  onChange={(e) => setCapture({ ...capture, first_name: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                  placeholder="Sarah"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#2C4C3B] font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="capture-email"
                  value={capture.email}
                  onChange={(e) => setCapture({ ...capture, email: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-[#2C4C3B] font-medium">
                  Phone <span className="text-xs text-[#5C6B62] font-normal">(optional)</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="capture-phone"
                  value={capture.phone}
                  onChange={(e) => setCapture({ ...capture, phone: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                  placeholder="(555) 123-4567"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-[#9CAF88]/10 border border-[#9CAF88]/20">
                <Checkbox
                  id="sms-opt"
                  data-testid="capture-sms-opt"
                  checked={capture.text_me_results}
                  onCheckedChange={(v) => setCapture({ ...capture, text_me_results: !!v })}
                  className="mt-0.5 border-[#2C4C3B] data-[state=checked]:bg-[#2C4C3B]"
                />
                <span className="text-sm text-[#2C4C3B]">
                  Also text me my results summary.
                </span>
              </label>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E07A5F] hover:bg-[#C96B52] text-white rounded-xl h-12 font-medium"
                data-testid="capture-submit"
              >
                {loading ? "Finding your match…" : "See my care recommendation"}
                {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>

              <div className="flex items-center gap-1.5 text-xs text-[#5C6B62] justify-center pt-1">
                <ShieldCheck className="w-3.5 h-3.5 text-[#9CAF88]" />
                Your information stays private — we never spam or sell data.
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Quiz FAQ — only shown on the first question to avoid distracting mid-quiz */}
      {step === 1 && (
        <FaqSection
          eyebrow="Before you start"
          title="What to expect"
          items={QUIZ_FAQ_ITEMS}
          testIdPrefix="quiz-faq"
        />
      )}
    </div>
  );
}
